/**
 * Identifica o perfil do usuário logado a partir do JWT e devolve os
 * identificadores relevantes para o filtro de "Histórico de Eventos".
 *
 * O endpoint `/historico-eventos` é único para todos os perfis (empresa,
 * estudante, CIEEMG). Para evitar mistura de registros entre empresas
 * ou entre estudantes, precisamos pré-aplicar o vínculo do usuário
 * logado (cpf, cnpj ou empresa_id) na query.
 *
 * Esta função foi adicionada para atender o item de aceite do card:
 *   "No perfil da empresa, o histórico deve apresentar exclusivamente
 *    os eventos relacionados à própria empresa, considerando o vínculo
 *    pelo ID da empresa."
 *
 * Estratégia (defesa em profundidade):
 *  1. Sempre extrai do JWT a melhor chave de vínculo disponível:
 *     - Estudante (nivel 1): `cpf` (11 dígitos).
 *     - Empresa (nivel 0): `empresa_id` (preferência) + CNPJ do
 *       estabelecimento (fallback, caso o backend ignore `empresa_id`).
 *     - CIEEMG (nivel >= 2): sem chave — vê tudo (visão geral).
 *  2. Retorna também o CNPJ bruto, para preencher o campo de filtro
 *     "FILTRAR POR EMPRESA (CPF/CNPJ)" com valor já formatado, igual
 *     ao que o backend espera no `cpf_cnpj` da query.
 *
 * Retorno: `{ isEstudante, isEmpresa, isCieemg, cpf, cnpj, empresaId, nivel }`.
 */
export function identificarPerfilHistorico(decoded) {
  const empty = {
    isEstudante: false,
    isEmpresa: false,
    isCieemg: false,
    cpf: "",
    cnpj: "",
    empresaId: "",
    nivel: 0,
  };
  if (!decoded || typeof decoded !== "object") return empty;

  const tipo = String(decoded.tipo || "").toLowerCase();
  const nivel = Number(decoded.acesso_autorizacao ?? 0);

  // Estudante: tipo === "estudante" OU nivel === 1.
  const isEstudante = tipo === "estudante" || nivel === 1;
  // CIEE/SECEX/SEOPE/INFOR: tipo === "usuario_ciee" OU nivel >= 2.
  const isCieemg = tipo === "usuario_ciee" || nivel >= 2;
  // Empresa: tudo que não é estudante nem CIEE e tem nivel 0.
  const isEmpresa = !isEstudante && !isCieemg;

  // CPF (11 dígitos) — usado para estudantes.
  const cpfBruto = String(
    decoded.cpf || decoded.documento || decoded.sub || "",
  )
    .replace(/\D/g, "")
    .slice(0, 11);

  // CNPJ (14 dígitos) — usado para empresas como fallback.
  const cnpjBruto = String(
    decoded.cnpj ||
      decoded.cnpj_empresa ||
      decoded.empresa_cnpj ||
      decoded.documento_empresa ||
      "",
  )
    .replace(/\D/g, "")
    .slice(0, 14);

  // empresa_id — vínculo forte pelo ID do estabelecimento.
  // Pode vir no JWT (`empresa_id`) ou ter sido gravado em localStorage
  // pelo Inicial (chave "empresa_id"). O caller faz o merge.
  const empresaId =
    decoded.empresa_id !== undefined && decoded.empresa_id !== null
      ? String(decoded.empresa_id)
      : "";

  return {
    isEstudante,
    isEmpresa,
    isCieemg,
    cpf: cpfBruto,
    cnpj: cnpjBruto,
    empresaId,
    nivel,
  };
}

/**
 * Versão "completa": recebe o JWT decodificado (de `jwtDecode`) e o
 * `localStorage` (caso o caller queira aplicar fallback de
 * `empresa_id` salvo pelo Inicial). Retorna o perfil já mesclado.
 *
 * @param {object|null} decoded Payload JWT decodificado.
 * @param {Storage|null} ls (opcional) `window.localStorage` para
 *   fallback de `empresa_id` em JWTs legados.
 */
export function identificarPerfilHistoricoComLS(decoded, ls) {
  const perfil = identificarPerfilHistorico(decoded);
  if (perfil.isEmpresa && !perfil.empresaId && ls) {
    try {
      const id = ls.getItem("empresa_id");
      if (id) perfil.empresaId = String(id);
    } catch (e) {
      /* silencioso */
    }
  }
  return perfil;
}
